/* global window console */
import { createAnalytics, createIdentityJwtTokenGetter, defineEvents, SHARED_EVENTS } from '@3plearning/mx-teacher-analytics';
import queryString from 'query-string';
import { getCountryCode, getLocale } from './helpers/utils';
import Resources from './helpers/resources';
import { appName } from './constants';

const TEACHER_TOTTER_PRODUCT = 'mathletics';
const CORE_GROUP = 'core';

export const events = defineEvents({
    ...SHARED_EVENTS,
    'assignpreference.saved': { group: 'settings', props: {} },
    'assignpreference.reset': { group: 'settings', props: {} },
    'privacysettings.saved': { group: 'settings', props: {} }
});

let client = null;

const readEnv = () => window._env_ || {};

const isEnabled = value => value === true || value === 'true';

// Empty tracking groups = null = every group fires. A configured list gates
// PostHog to those groups, always including the shared `core` group so the
// page-view event keeps firing.
const resolveEnabledGroups = (trackingGroups) => {
    if (!trackingGroups) {
        return null;
    }
    const groups = trackingGroups.split(',').map(group => group.trim()).filter(Boolean);
    return [...groups, CORE_GROUP];
};

const getIdentity = () => {
    const params = queryString.parse(window.location.search);
    return {
        locale: getLocale(),
        countryCode: getCountryCode(),
        userProfileId: params.username || '',
        userProfileType: 'unknown',
        marketRegionId: ''
    };
};

// teacher-totter authenticates with an identity JWT minted from the app's auth
// token; the resource environment holds that token once the app has configured.
const getAppAuthToken = () => (Resources.env && Resources.env.authToken) || null;

// teacher-totter is only permitted for OneRoster-activated schools.
const isOneRosterActivatedSchool = () =>
    queryString.parse(window.location.search).isOneRosterActivatedSchool === 'true';

// Analytics must never break the host app, so initialisation is fully guarded.
export const initAnalytics = () => {
    if (client) {
        return client;
    }

    const env = readEnv();

    try {
        client = createAnalytics({
            app: appName,
            environment: env.ENVIRONMENT || 'live',
            providers: {
                posthog: isEnabled(env.POSTHOG_ENABLED) && env.POSTHOG_KEY && env.POSTHOG_HOST
                    ? { enabled: true, apiKey: env.POSTHOG_KEY, host: env.POSTHOG_HOST }
                    : { enabled: false },
                teacherTotter: {
                    enabled: isEnabled(env.TEACHER_TOTTER_ENABLED)
                        && Boolean(env.TEACHER_TOTTER_SYNC_URL)
                        && isOneRosterActivatedSchool(),
                    syncUrl: env.TEACHER_TOTTER_SYNC_URL || '',
                    product: TEACHER_TOTTER_PRODUCT,
                    getAuthToken: createIdentityJwtTokenGetter({
                        identityGatewayUrl: env.IDENTITY_GATEWAY_URL || '',
                        getAppAuthToken
                    })
                }
            },
            enabledFeatureGroups: resolveEnabledGroups(env.POSTHOG_TRACKING_GROUPS),
            events
        });

        client.setCommonProps(getIdentity());
        client.identify();
        client.track('page.view', {});
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('initAnalytics() - analytics failed to initialise', error);
    }

    return client;
};

export const getAnalytics = () => client;

// Emit a CRUD event for a service call, tagged with its outcome.
export const trackServiceEvent = (name, status) => {
    if (!client) {
        return;
    }
    client.track(name, { result: status === 200 ? 'success' : 'failure' });
};



// WEBPACK FOOTER //
// ./app/analytics.js